import { Collapse, Offcanvas } from 'bootstrap';

function handleOffcanvasEvents(): void {
  const offcanvasOffsetElement = document.querySelector<HTMLElement>('header');
  const offcanvasButton = document.getElementById('headerOffcanvasButton');
  const offcanvas = document.getElementById('headerOffcanvas');

  if (offcanvasOffsetElement && offcanvasButton && offcanvas) {
    offcanvas.addEventListener('show.bs.offcanvas', () => {
      offcanvasButton.classList.add('offcanvas-open');
      const topOffset = offcanvasOffsetElement.getBoundingClientRect().bottom;
      offcanvas.style.top = `${topOffset}px`;
    });

    offcanvas.addEventListener('hide.bs.offcanvas', () => {
      offcanvasButton.classList.remove('offcanvas-open');
    });
  }
}

function handleMainNavigationEvents(): void {
  const mainNavigationCollapses = document.querySelectorAll<HTMLElement>('.navigation-menu-dropdowns .collapse');
  const mainNavigationItemsContainer = document.querySelectorAll<HTMLElement>('.main-navigation-items');
  let collapseOpenCount = 0;

  if (mainNavigationCollapses.length && mainNavigationItemsContainer.length) {
    mainNavigationCollapses.forEach(collapse => {
      collapse.addEventListener('hide.bs.collapse', () => {
        collapseOpenCount--;
        if (collapseOpenCount <= 0) {
          mainNavigationItemsContainer.forEach(container => container.classList.remove('dropdown-open'));
        }
      });
      collapse.addEventListener('show.bs.collapse', () => {
        collapseOpenCount++;
        if (collapseOpenCount > 0) {
          mainNavigationItemsContainer.forEach(container => container.classList.add('dropdown-open'));
        }
      });
    });
  }
}

function handleKeyboardAndMouseEvents(): void {
  // On escape keypress, collapse all header collapses
  const headerCollapses = document.querySelectorAll<HTMLElement>('header .collapse, .main-navigation-wrapper .collapse');
  if (headerCollapses.length) {
    document.addEventListener('keydown', (event) => {
      if (event?.key === 'Escape' || event?.key === 'Esc') {
        headerCollapses.forEach((headerCollapse) => {
          const collapse = Collapse.getOrCreateInstance(headerCollapse, { toggle: false });
          collapse.hide();
        });
      }
    });
  }

  // On click outside main navigation, close all main navigation collapses
  const mainNavigationWrappers = Array.from(document.querySelectorAll('.main-navigation'));
  const mainNavigationCollapses = document.querySelectorAll<HTMLElement>('.main-navigation .collapse');
  if (mainNavigationWrappers.length && mainNavigationCollapses.length) {
    document.addEventListener('click', (event) => {
      const clickedMainNavigation = mainNavigationWrappers.some(wrapper => wrapper.contains(event.target as Node));
      if (!clickedMainNavigation) {
        mainNavigationCollapses.forEach((mainNavigationCollapse) => {
          const collapse = Collapse.getOrCreateInstance(mainNavigationCollapse, { toggle: false });
          collapse.hide();
        });
      }
    });
  }

  // On click outside header and main navigation, close header offcanvas
  const header = document.querySelector<HTMLElement>('header');
  const headerOffcanvas = document.querySelector<HTMLElement>('#headerOffcanvas');
  if (header && headerOffcanvas) {
    document.addEventListener('click', (event) => {
      if (!header.contains(event.target as Node) && !mainNavigationWrappers.some(wrapper => wrapper.contains(event.target as Node))) {
        const offcanvasInstance = Offcanvas.getOrCreateInstance(headerOffcanvas, { toggle: false });
        offcanvasInstance.hide();
      }
    });
  }
}

const UTILITY_NAVIGATION_CROWDED_CLASS = 'utility-navigation-crowded';

/**
 * Hides the utility bar's nav links whenever they and the editor-controlled CTAs
 * can no longer share a single line, and restores them once there is room again.
 */
function handleUtilityNavigationCrowding(): void {
  const navigations = document.querySelectorAll<HTMLElement>('.header-utility-bar .utility-navigation');
  if (!navigations.length || typeof ResizeObserver === 'undefined') {
    return;
  }

  /** Re-evaluates one utility bar and applies or clears the crowded state class. */
  const update = (navigation: HTMLElement): void => {
    const items = navigation.querySelector<HTMLElement>('.utility-navigation-items');
    if (!items) {
      return;
    }

    // Always measure the uncrowded layout: reading while the class is applied would
    // report no overflow (links hidden) and flip the state back on every pass.
    navigation.classList.remove(UTILITY_NAVIGATION_CROWDED_CLASS);

    // Zero width means CSS already hides the links (below `sm`) or there are none,
    // so there is nothing for this behaviour to do. Both reads force one sync layout.
    const hasVisibleItems = items.offsetWidth > 0;
    const overflows = navigation.scrollWidth > navigation.clientWidth + 1;

    navigation.classList.toggle(UTILITY_NAVIGATION_CROWDED_CLASS, hasVisibleItems && overflows);
  };

  // Batching into a frame keeps the class write out of the observer callback,
  // which is what avoids "ResizeObserver loop" errors.
  let frameId = 0;
  const schedule = (): void => {
    if (frameId) {
      return;
    }
    frameId = window.requestAnimationFrame(() => {
      frameId = 0;
      navigations.forEach(update);
    });
  };

  const observer = new ResizeObserver(schedule);
  navigations.forEach((navigation) => {
    update(navigation);
    observer.observe(navigation);

    // Also watch the links themselves so a late webfont swap or CTA change re-triggers.
    const items = navigation.querySelector<HTMLElement>('.utility-navigation-items');
    if (items) {
      observer.observe(items);
    }
  });
}

/**
 * Publishes the height of everything above the main nav bar as --header-sticky-offset, which
 * "Navigation" sticky mode applies as a negative top offset so the utility bar scrolls away and
 * the nav alone pins. Measured rather than hard-coded because the utility bar wraps and hides its
 * links entirely once the CTAs crowd it.
 */
function handleStickyOffset(): void {
  const header = document.querySelector<HTMLElement>('header.header-sticky-navigation');
  const nav = header?.querySelector<HTMLElement>('.header-main-nav');
  if (!header || !nav || typeof ResizeObserver === 'undefined') {
    return;
  }

  // Both rects shift together once the header sticks, so their difference stays the true
  // distance from the top of the header to the top of the nav regardless of scroll position.
  const update = (): void => {
    const offset = nav.getBoundingClientRect().top - header.getBoundingClientRect().top;
    header.style.setProperty('--header-sticky-offset', `${Math.max(0, Math.round(offset))}px`);
  };

  // Batching into a frame keeps the style write out of the observer callback,
  // which is what avoids "ResizeObserver loop" errors.
  let frameId = 0;
  const schedule = (): void => {
    if (frameId) {
      return;
    }
    frameId = window.requestAnimationFrame(() => {
      frameId = 0;
      update();
    });
  };

  update();
  const observer = new ResizeObserver(schedule);
  observer.observe(header);
  observer.observe(nav);
}

document.addEventListener('DOMContentLoaded', () => {
  handleOffcanvasEvents();
  handleMainNavigationEvents();
  handleKeyboardAndMouseEvents();
  handleUtilityNavigationCrowding();
  handleStickyOffset();
});
